import { BlotterTable, useBehaviorSubject, useBlotterTable, type ColumnDef, type CustomerQuote } from '@talos/kyoko';
import { useMemo } from 'react';

interface CustomerQuotesToFixBlotterProps {
  customerQuotesToFix: CustomerQuote[];
}

export const CustomerQuotesToFixBlotter = ({ customerQuotesToFix }: CustomerQuotesToFixBlotterProps) => {
  const { observable: dataObservable } = useBehaviorSubject(
    () => ({ initial: true, data: customerQuotesToFix }),
    [customerQuotesToFix]
  );

  const columns = useCustomerQuotesToFixColumns();

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable: undefined,
    rowID: 'RFQID' satisfies keyof CustomerQuote,
    columns,
    gridOptions: {
      suppressHeaderFocus: true,
    },
  });

  return <BlotterTable {...blotterTable} />;
};

const useCustomerQuotesToFixColumns = () => {
  const columns = useMemo(
    () =>
      [
        /* The widths below are specifically set to fit well in the fixing dialog's blotter */
        { type: 'date', field: 'SubmitTime', title: 'Submit Time', width: 130 },
        { type: 'id', field: 'RFQID', width: 80 },
        { type: 'customer', title: 'Counterparty', field: 'Counterparty', width: 110 },
        { field: 'tradedSideOrSide', title: 'Side', type: 'quoteSide', width: 60 },
        { field: 'symbolOrSymbols', type: 'security', title: 'Symbol', width: 120 },
        { field: 'TradedQty', type: 'size', params: { currencyField: 'Currency' }, width: 120 },
        { field: 'fixingDate', type: 'dateOnly', title: 'Fixing Date', width: 110 },
        { field: 'PricingReference', type: 'price', params: { isReferencePrice: true }, width: 130 },
      ] satisfies ColumnDef<CustomerQuote>[],
    []
  );

  return columns;
};
